import { useGetAdmins } from "@entities/admin";
import { useBriefStore } from "@pages/layout/BriefLayout/lib/useLayoutStore";
import {
  IAssignPricingPocRequest,
  IPricingWorkload,
} from "@shared/lib/types/pricingWorkload";
import { CircularProgress, Typography } from "@shared/ui";
import { Button } from "@shared/ui/Button";
import { Input } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useDebounce } from "use-debounce";
import { BriefPricingPocTags } from "./components/BriefPricingPocTags";
import { DeadlinePicker, DeadlinePreset } from "./components/DeadlinePicker";
import { DefaultPricingPocCards } from "./components/DefaultPricingPocCards";
import { EmployeeSearchInput } from "./components/EmployeeSearchInput";
import { PocWorkloadList } from "./components/PocWorkloadList";
import { RemovePricingBody } from "./components/RemovePricingBody";
import styles from "./styles.module.scss";

const { Text, Paragraph } = Typography;

const EMPLOYEE_SEARCH_LIMIT = 10;

export type AssignPricingModalType = "assign" | "reassign" | "remove";

interface AssignPricingModalV2Props {
  briefInfluencerIds: string[];
  onClose: () => void;
  isLoading: boolean;
  onConfirm: (data: Omit<IAssignPricingPocRequest, "briefId">) => void;
  type: AssignPricingModalType;
  selectAll?: boolean;
  uncheckList?: string[];
  totalCount?: number;
}

const MODE_COPY: Record<
  Exclude<AssignPricingModalType, "remove">,
  {
    description: string;
    listTitle: string;
    confirmLabel: string;
    summaryAction: string;
  }
> = {
  assign: {
    description:
      "eligible for internal pricing will be allocated for rate capture (commercials pending, negotiating, or awaiting a quote). The POC you pick can enter prices in the Pricing module before the deadline.",
    listTitle: "Assign to pricing POC",
    confirmLabel: "Confirm allocation",
    summaryAction: "queued for pricing",
  },
  reassign: {
    description:
      "on the pricing queue will be reassigned. Priced commercials and negotiation history stay on the row — only the owning POC changes.",
    listTitle: "Reassign to pricing POC",
    confirmLabel: "Confirm reallocation",
    summaryAction: "reallocated",
  },
};

export function AssignPricingModalV2({
  briefInfluencerIds,
  onClose,
  isLoading,
  onConfirm,
  type,
  selectAll,
  uncheckList,
  totalCount,
}: AssignPricingModalV2Props) {
  const { brief } = useBriefStore();
  const defaultPricingPoc = useMemo(() => {
    return brief?.defaultPricingPoc;
  }, [brief?.defaultPricingPoc]);
  const defaultAgencyPricingPoc = useMemo(() => {
    return brief?.defaultAgencyPricingPoc;
  }, [brief?.defaultAgencyPricingPoc]);
  const defaultAgencyPricingPocId = defaultAgencyPricingPoc?.userId;

  const [searchQuery, setSearchQuery] = useState(
    `${defaultPricingPoc?.user?.firstName ?? ""} ${defaultPricingPoc?.user?.lastName ?? ""}`.trim() ||
      ""
  );
  const [debouncedSearchQuery] = useDebounce(searchQuery, 250);

  const { admins, isFetching } = useGetAdmins(
    EMPLOYEE_SEARCH_LIMIT,
    1,
    undefined,
    debouncedSearchQuery,
    undefined,
    undefined,
    undefined,
    true,
    type !== "remove"
  );

  const [selectedSpocId, setSelectedSpocId] = useState(
    defaultPricingPoc?.userId || ""
  );
  const [pricingOutreachTerms, setPricingOutreachTerms] = useState(
    brief?.pricingOutreachTerms || ""
  );
  const [preset, setPreset] = useState<DeadlinePreset>("48h");
  const [customDate, setCustomDate] = useState<Dayjs | null>(
    dayjs().add(2, "day")
  );

  const briefPocUserIds = useMemo(
    () => brief?.pricing?.allocatedTo?.map((item) => item.userId) ?? [],
    [brief?.pricing?.allocatedTo]
  );

  const assignedPocTags = useMemo(
    () =>
      brief?.pricing?.allocatedTo?.map((item) => ({
        spocId: item.userId,
        name: `${item.user.firstName ?? ""} ${item.user.lastName ?? ""}`.trim(),
      })) ?? [],
    [brief?.pricing?.allocatedTo]
  );

  const deadlineTo = useMemo(() => {
    if (preset === "24h") return dayjs().add(24, "hour");
    if (preset === "48h") return dayjs().add(48, "hour");
    return customDate;
  }, [preset, customDate]);

  const eligibleCount =
    selectAll && totalCount
      ? totalCount - (uncheckList?.length || 0)
      : briefInfluencerIds.length;
  const workloads = useMemo(
    () =>
      (admins?.data?.map((admin) => admin.pricingWorkload).filter(Boolean) ??
        []) as IPricingWorkload[],
    [admins?.data]
  );

  useEffect(() => {
    if (brief?.pricingOutreachTerms) {
      setPricingOutreachTerms(brief?.pricingOutreachTerms);
    }
  }, [brief?.pricingOutreachTerms]);

  if (type === "remove") {
    return (
      <RemovePricingBody
        eligibleCount={eligibleCount}
        isLoading={isLoading}
        onClose={onClose}
        onConfirm={() =>
          onConfirm({
            briefInfluencerIds,
            assignedToUserId: "",
            deadlineFrom: dayjs().toISOString(),
            deadlineTo: dayjs().toISOString(),
          })
        }
      />
    );
  }

  const isReassign = type === "reassign";
  const copy = MODE_COPY[type];

  const selectedPoc = workloads.find((w) => w?.user._id === selectedSpocId);

  const isConfirmDisabled =
    !selectedSpocId ||
    eligibleCount === 0 ||
    !deadlineTo ||
    !deadlineTo.isAfter(dayjs());

  const handleSubmit = () => {
    if (isConfirmDisabled || !deadlineTo) return;
    onConfirm({
      briefInfluencerIds,
      assignedToUserId: selectedSpocId,
      deadlineFrom: dayjs().toISOString(),
      deadlineTo: deadlineTo.toISOString(),
      pricingOutreachTerms,
    });
  };

  return (
    <div className={styles.container}>
      <Paragraph className={styles.description}>
        <Text strong>{eligibleCount}</Text> selected creator
        {eligibleCount !== 1 ? "s" : ""} {copy.description}
      </Paragraph>

      <div className={styles.section}>
        <BriefPricingPocTags
          tags={assignedPocTags}
          onClick={(value) => {
            setSearchQuery(value);
          }}
        />

        <DefaultPricingPocCards
          defaultPricingPoc={defaultPricingPoc}
          defaultAgencyPricingPoc={defaultAgencyPricingPoc}
          onSelect={(name, userId) => {
            setSearchQuery(name);
            setSelectedSpocId(userId);
          }}
        />

        <div className={styles.sectionTitle}>{copy.listTitle}</div>

        <EmployeeSearchInput value={searchQuery} onChange={setSearchQuery} />

        {isFetching ? (
          <div className={styles.loader}>
            <CircularProgress />
          </div>
        ) : (
          <PocWorkloadList
            defaultPricingPocId={defaultPricingPoc?.userId || ""}
            defaultAgencyPricingPocId={defaultAgencyPricingPocId || ""}
            workloads={workloads}
            selectedSpocId={selectedSpocId}
            onSelect={setSelectedSpocId}
            briefPocUserIds={briefPocUserIds}
          />
        )}

        {selectedPoc ? (
          <Text type="secondary" className={styles.selectedPocSummary}>
            <Text strong>
              {selectedPoc.user.firstName} {selectedPoc.user.lastName}
            </Text>{" "}
            has{" "}
            <Text strong>{selectedPoc.activeBriefInfluencersPendingCount}</Text>{" "}
            creator
            {selectedPoc.activeBriefInfluencersPendingCount !== 1
              ? "s"
              : ""}{" "}
            still awaiting saved commercials.
          </Text>
        ) : null}
      </div>

      {isReassign ? (
        <div className={styles.infoBanner}>
          Creators keep their saved commercials. The new POC sees them in{" "}
          <span className={styles.link}>My Pricing</span> and in the brief
          reallocation log.
        </div>
      ) : null}

      <DeadlinePicker
        preset={preset}
        onChangePreset={setPreset}
        customDate={customDate}
        onChangeCustomDate={setCustomDate}
      />

      <div className={styles.section}>
        <div className={styles.sectionTitle}>Notes for Pricing Team</div>
        <Input.TextArea
          value={pricingOutreachTerms}
          onChange={(e) => setPricingOutreachTerms(e.target.value)}
          placeholder="Add notes for pricing team..."
          autoSize={{ minRows: 2, maxRows: 6 }}
        />
      </div>

      <div
        className={`${styles.summary} ${
          eligibleCount === 0 ? styles.summaryWarning : ""
        }`}
      >
        {eligibleCount === 0 ? (
          <Text>
            <Text strong>No eligible creators</Text> in the current selection.
            Close and pick other rows.
          </Text>
        ) : (
          <Text>
            <Text strong>{eligibleCount}</Text> creator
            {eligibleCount !== 1 ? "s" : ""} will be {copy.summaryAction}.
          </Text>
        )}
      </div>

      <div className={styles.actions}>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          type="primary"
          loading={isLoading}
          disabled={isConfirmDisabled}
          onClick={handleSubmit}
        >
          {copy.confirmLabel}
        </Button>
      </div>
    </div>
  );
}
